/**
 * Email typo suggestion ("hotmil.com" → "hotmail.com"). Same domains and algorithm as the tour registration
 * (margarita.pasosdegigante.com, src/lib/correo.ts) and the booking calendar, so a lead sees the same help everywhere.
 */
const DOMAINS = [
  "gmail.com", "hotmail.com", "hotmail.es", "hotmail.com.mx", "outlook.com", "outlook.es", "outlook.com.mx",
  "live.com", "live.com.mx", "yahoo.com", "yahoo.com.mx", "yahoo.es", "icloud.com", "me.com", "msn.com", "aol.com",
  "prodigy.net.mx", "protonmail.com", "proton.me",
  // Real domains close to the ones above: without them they would be "corrected" by mistake.
  "mail.com", "email.com", "gmx.com", "ymail.com", "live.co.uk", "hotmail.fr", "yahoo.fr"
];

/** Damerau-Levenshtein distance (a transposition, "gmial", counts as a single error). */
function distance(a: string, b: string): number {
  const d = Array.from({ length: a.length + 1 }, (_, i) =>
    Array.from({ length: b.length + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0)));
  for (let i = 1; i <= a.length; i++)
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i]![j] = Math.min(d[i - 1]![j]! + 1, d[i]![j - 1]! + 1, d[i - 1]![j - 1]! + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1])
        d[i]![j] = Math.min(d[i]![j]!, d[i - 2]![j - 2]! + 1);
    }
  return d[a.length]![b.length]!;
}

/** The corrected address, or `null` when the domain is known or not close to any known one. */
export function suggestEmail(value: string): string | null {
  const email = value.trim();
  const at = email.lastIndexOf("@");
  if (at < 1) return null;
  const user = email.slice(0, at);
  const typed = email.slice(at + 1).toLowerCase();
  const domain = typed.replace(/\.+$/, "");
  if (!domain) return null;
  // "gmail.com." (trailing dot): the domain is right, only the dot is extra.
  if (DOMAINS.includes(domain)) return domain === typed ? null : `${user}@${domain}`;
  let best: string | null = null;
  let lowest = Infinity;
  for (const known of DOMAINS) {
    const d = distance(domain, known);
    if (d < lowest) [best, lowest] = [known, d];
  }
  const allowed = domain.length >= 9 ? 2 : 1;
  return best && lowest <= allowed ? `${user}@${best}` : null;
}
